import { Combobox } from "../ui";
import { useTranslation } from "react-i18next";
import { useCompaniesStore, type Company } from "../../store/admin/companiesStore";

interface SelectOption {
  value: string;
  label: string;
}

/**
 * Genera las opciones del selector a partir de un array de compañías.
 * Usa una lógica de label consistente en toda la app.
 */
export function buildCompanyOptions(companies: Company[]): SelectOption[] {
  return companies.map((company) => ({
    value: company.id,
    label: company.name || company.business_name || company.id,
  }));
}

interface CompanySelectorProps {
  /** ID de la compañía actualmente seleccionada */
  value: string | null | undefined;
  /** Callback cuando cambia la selección */
  onChange: (companyId: string | null) => void;
  /** Modo oscuro */
  /** Permitir limpiar la selección */
  isClearable?: boolean;
  /** Mostrar indicador de error */
  hasError?: boolean;
  /** Deshabilitar el selector */
  isDisabled?: boolean;
  /** Mostrar indicador de carga */
  isLoading?: boolean;
  /** Texto de placeholder personalizado */
  placeholder?: string;
  /** Clase CSS adicional para el contenedor */
  className?: string;
  /** Compañías a mostrar (si no se pasan, usa allCompanies del store) */
  companies?: Company[];
  menuPlacement?: "auto" | "top" | "bottom";
}

export default function CompanySelector({
  value,
  onChange,
  isClearable = false,
  hasError = false,
  isDisabled = false,
  isLoading = false,
  placeholder,
  className,
  companies: companiesProp,
  menuPlacement = "auto",
}: CompanySelectorProps) {
  const { t } = useTranslation("common");

  // Si no se pasan compañías por prop, usar las del store
  const storeCompanies = useCompaniesStore((state) => state.allCompanies);
  const storeLoading = useCompaniesStore((state) => state.allCompaniesLoading);

  const companies = companiesProp ?? storeCompanies;
  const loading = isLoading || storeLoading;
  const options = buildCompanyOptions(companies);

  const selectedOption = options.find((opt) => opt.value === value) ?? null;

  return (
    <div className={className}>
      <Combobox<SelectOption>
        value={selectedOption}
        onChange={(option) => onChange(option?.value ?? null)}
        options={options}
        placeholder={placeholder ?? t("header.selectCompany")}
        isSearchable
        isClearable={isClearable}
        isDisabled={isDisabled}
        isLoading={loading}
        hasError={hasError}
        noOptionsMessage={() => t("header.noCompanies")}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement={menuPlacement}
      />
    </div>
  );
}
